import type { ConfigType } from 'dayjs'
import type {
  AuditLogChange,
  AuditLogEvent,
  AuditLogFloristNotification,
} from '@/lib/services/audit-log/audit-log.types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { EmptyState } from '@/components/patterns/empty-state'
import { Badge, type BadgeVariant } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Typography } from '@/components/ui/typography'
import { formatPhone } from '@/hooks/use-phone-formatter'
import dayjs from '@/lib/dayjs'
import { useAuditLog } from '@/lib/services/audit-log/audit-log.query'
import { cn } from '@/lib/utils'
import { ArrowRight, ChevronDown, CircleAlert, Clock3, FileClock, RefreshCw } from 'lucide-react'

export type AuditHistoryTimelineProps = Pick<
  Parameters<typeof useAuditLog>[0],
  'subject' | 'recordId' | 'organizationId' | 'pageSize'
>
// Retain the renderer's existing created_at fallback without changing the API event type.
type HistoryEvent = AuditLogEvent & { created_at?: string | null }
type RetryProps = { onRetry: () => unknown; isRetrying: boolean }

const notificationStatusVariants: Partial<Record<string, BadgeVariant>> = {
  accepted: 'info',
  bounced: 'destructive',
  canceled: 'muted',
  delivered: 'success',
  failed: 'destructive',
  failure: 'destructive',
  opened: 'success',
  partially_delivered: 'warning',
  queued: 'info',
  read: 'success',
  received: 'success',
  receiving: 'info',
  retrying: 'warning',
  scheduled: 'info',
  sending: 'info',
  sent: 'info',
  success: 'success',
  unacknowledged: 'warning',
  unavailable: 'muted',
  undelivered: 'destructive',
}

function getNotificationStatusVariant(status: AuditLogFloristNotification['status']) {
  return (status == null ? undefined : notificationStatusVariants[status]) || 'muted'
}

function formatNotificationDestination(notification: AuditLogFloristNotification): string {
  const destination = String(notification?.destination || '').trim()

  if (!destination) {
    return ''
  }

  const phoneChannels: readonly AuditLogFloristNotification['channel'][] = ['fax', 'phone', 'text']
  return phoneChannels.includes(notification.channel) ? formatPhone(destination) : destination
}

function getNotificationDetails(notification: AuditLogFloristNotification): string {
  const channelLabel = notification.channel_label || 'Notification'
  const attemptsLabel = notification.attempts_label || 'Attempt count unavailable'
  const destination = formatNotificationDestination(notification)
  const details = [destination ? `${channelLabel} to ${destination}` : channelLabel]

  details.push(attemptsLabel)

  return details.join(' · ')
}

function formatAuditTimestamp(timestamp: ConfigType): string {
  if (!timestamp) {
    return 'Time unavailable'
  }

  const parsedTimestamp = dayjs(timestamp)

  return parsedTimestamp.isValid()
    ? parsedTimestamp.format('MMM D, YYYY [at] h:mm A')
    : 'Time unavailable'
}

function formatChangeValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return 'Not set'
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }

  return 'Updated'
}

function AuditChange({ change }: { change: AuditLogChange }) {
  const label = change.label || 'Record detail'
  const subject = change.subject
  const isChangedMarker =
    (change.from === null || change.from === undefined) && change.to === 'Changed'
  const emptyValues: readonly unknown[] = [null, undefined, '']
  const hasFrom = !emptyValues.includes(change.from)
  const hasTo = !emptyValues.includes(change.to)
  const collectionChange =
    change.key === 'collections' && !hasFrom && hasTo
      ? `Added to ${formatChangeValue(change.to)}`
      : change.key === 'collections' && hasFrom && !hasTo
        ? `Removed from ${formatChangeValue(change.from)}`
        : null

  return (
    <div className="grid min-w-0 gap-2 rounded-lg border border-border/60 bg-background/70 px-3 py-2.5 @[36rem]/audit-history:grid-cols-[minmax(12rem,1fr)_auto] @[36rem]/audit-history:items-center @[36rem]/audit-history:gap-6">
      <div className="min-w-0">
        {subject ? (
          <Typography variant="caption" weight="medium" className="block truncate">
            {subject}
          </Typography>
        ) : null}
        <Typography variant="label" className="block break-words">
          {label}
        </Typography>
      </div>
      <div className="flex min-w-0 items-center gap-2 text-sm @[36rem]/audit-history:justify-end @[36rem]/audit-history:gap-3">
        {isChangedMarker || collectionChange ? (
          <span className="min-w-0 break-words font-medium text-foreground">
            {collectionChange || 'Changed'}
          </span>
        ) : (
          <>
            <span className="min-w-0 flex-1 break-words text-muted-foreground @[36rem]/audit-history:max-w-56 @[36rem]/audit-history:flex-none @[36rem]/audit-history:text-right">
              {formatChangeValue(change.from)}
            </span>
            <ArrowRight className="size-4 shrink-0 text-muted-foreground/60" aria-hidden="true" />
            <span className="min-w-0 flex-1 break-words font-medium text-foreground @[36rem]/audit-history:max-w-56 @[36rem]/audit-history:flex-none @[36rem]/audit-history:text-right">
              {formatChangeValue(change.to)}
            </span>
          </>
        )}
      </div>
    </div>
  )
}

function FloristNotificationDetails({
  notification,
}: {
  notification?: AuditLogFloristNotification | null
}) {
  if (!notification) {
    return null
  }

  return (
    <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2">
      <Typography variant="caption">Current status</Typography>
      <Badge variant={getNotificationStatusVariant(notification.status)} size="status">
        {notification.status_label || 'Status unavailable'}
      </Badge>
      <Typography variant="caption" className="min-w-0 break-words">
        {getNotificationDetails(notification)}
      </Typography>
    </div>
  )
}

function AuditEvent({ event, isLast }: { event: HistoryEvent; isLast: boolean }) {
  const changes = Array.isArray(event.changes) ? event.changes : []
  const actorName = event.actor?.name || 'Automated update'
  const summary = event.summary || 'Updated this record.'
  const isFloristNotification = event.action === 'florist_notification'
  const timestampLabel = isFloristNotification
    ? event.notification?.timestamp_label || 'Recorded activity'
    : actorName

  return (
    <li className="grid min-w-0 grid-cols-[1.75rem_minmax(0,1fr)] gap-2.5">
      <div className="flex flex-col items-center" aria-hidden="true">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/8 text-interaction ring-3 ring-background dark:border-primary/30 dark:bg-primary/12">
          <Clock3 className="size-3" />
        </span>
        {isLast ? null : <span className="my-1.5 w-px grow bg-border/70" />}
      </div>

      <div className={cn('min-w-0', isLast ? '' : 'pb-4')}>
        <Typography variant="bodySmall" weight="medium" className="min-w-0 break-words">
          {summary}
        </Typography>
        {isFloristNotification ? (
          <FloristNotificationDetails notification={event.notification} />
        ) : null}
        <Typography variant="caption" className="mt-0.5 block min-w-0 break-words">
          {timestampLabel} · {formatAuditTimestamp(event.occurred_at ?? event.created_at)}
        </Typography>

        {changes.length > 0 ? (
          <Collapsible className="group mt-2">
            <CollapsibleTrigger asChild>
              <Button variant="ghostMuted" size="micro" className="-ml-1.5">
                {changes.length} {changes.length === 1 ? 'change' : 'changes'}
                <ChevronDown
                  className="size-3.5 transition-transform group-data-[state=open]:rotate-180"
                  aria-hidden="true"
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-1.5">
              <div className="grid gap-1.5">
                {changes.map((change, index) => (
                  <AuditChange
                    change={change}
                    key={`${event.id}-${change.key || 'change'}-${index}`}
                  />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        ) : null}
      </div>
    </li>
  )
}

function AuditHistoryError({ onRetry, isRetrying }: RetryProps) {
  return (
    <Alert variant="destructive" className="m-4 sm:m-5">
      <CircleAlert aria-hidden="true" />
      <AlertTitle>History could not be loaded</AlertTitle>
      <AlertDescription>
        <p>Please try again. Your record has not been changed.</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-2"
          disabled={isRetrying}
          onClick={onRetry}
        >
          <RefreshCw className={cn('size-3.5', isRetrying && 'animate-spin')} aria-hidden="true" />
          {isRetrying ? 'Trying again...' : 'Try again'}
        </Button>
      </AlertDescription>
    </Alert>
  )
}

function AuditHistoryEmpty() {
  return (
    <EmptyState
      className="m-4 sm:m-5"
      icon={FileClock}
      title="No history yet"
      description="Meaningful changes to this record will appear here."
    />
  )
}

function AuditHistoryLoading() {
  return (
    <div
      className="m-4 flex min-h-40 items-center justify-center sm:m-5"
      role="status"
      aria-label="Loading history"
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <RefreshCw className="size-4 animate-spin" aria-hidden="true" />
        <Typography variant="supporting">Loading history...</Typography>
      </div>
    </div>
  )
}

function AuditHistoryPaginationError({ onRetry, isRetrying }: RetryProps) {
  return (
    <div className="border-t border-border/60 pt-5">
      <Alert variant="destructive">
        <CircleAlert aria-hidden="true" />
        <AlertTitle>Older activity could not be loaded</AlertTitle>
        <AlertDescription>
          <p>The activity already shown is still available.</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            disabled={isRetrying}
            onClick={onRetry}
          >
            <RefreshCw
              className={cn('size-3.5', isRetrying && 'animate-spin')}
              aria-hidden="true"
            />
            {isRetrying ? 'Trying again...' : 'Try loading older activity again'}
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  )
}

export default function AuditHistoryTimeline({
  subject,
  recordId,
  organizationId,
  pageSize = 20,
}: AuditHistoryTimelineProps) {
  const auditLogQuery = useAuditLog({
    subject,
    recordId,
    organizationId,
    pageSize,
  })

  const events = auditLogQuery.data?.pages.flatMap((page) => page?.data ?? []) ?? []

  if (auditLogQuery.isError && events.length === 0) {
    return (
      <AuditHistoryError
        isRetrying={auditLogQuery.isFetching}
        onRetry={() => auditLogQuery.refetch()}
      />
    )
  }

  if (events.length === 0 && (auditLogQuery.isPending || auditLogQuery.isFetching)) {
    return <AuditHistoryLoading />
  }

  if (events.length === 0) {
    return <AuditHistoryEmpty />
  }

  return (
    <div className="@container/audit-history min-w-0 p-4 sm:p-5">
      <ol aria-label="Record history">
        {events.map((event, index) => (
          <AuditEvent event={event} isLast={index === events.length - 1} key={event.id} />
        ))}
      </ol>

      {auditLogQuery.isFetchNextPageError ? (
        <AuditHistoryPaginationError
          isRetrying={auditLogQuery.isFetchingNextPage}
          onRetry={() => auditLogQuery.fetchNextPage()}
        />
      ) : auditLogQuery.hasNextPage ? (
        <div className="flex justify-center border-t border-border/60 pt-5">
          <Button
            variant="outline"
            size="sm"
            disabled={auditLogQuery.isFetchingNextPage}
            onClick={() => auditLogQuery.fetchNextPage()}
          >
            {auditLogQuery.isFetchingNextPage ? (
              <RefreshCw className="size-3.5 animate-spin" aria-hidden="true" />
            ) : null}
            {auditLogQuery.isFetchingNextPage ? 'Loading activity...' : 'Load older activity'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export { formatAuditTimestamp, formatChangeValue }
